import React, { useState } from 'react';
import { formatOrderRef } from '../../utils/orderRef';
import { ChevronDown, ChevronRight, FileText, Download, Phone, Mail, MapPin, Eye, User, Box } from 'lucide-react';
import StatusSelect from './StatusSelect';
import NotificationBadge from '../common/NotificationBadge';
import FilePreview from '../common/FilePreview';
import { PROSTHESIS_TYPE_LABELS, getSubtypeLabel } from '../../utils/constants';
import { filesApi } from '../../services/api';
import { formatPatientDisplay } from '../../utils/nameNormalizer';

const OrderTableRow = ({ order, onStatusChange, onOrderClick, onPatientClick, unreadCount = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleDownload = async (file) => {
        if (!file.id) {
            console.log(`Impossible de télécharger le fichier: ${file.name} - ID manquant`);
            return;
        }

        try {
            console.log('Téléchargement du fichier avec authentification:', file.id);
            
            // Utiliser l'API authentifiée pour télécharger le fichier
            const blob = await filesApi.downloadBlob(file.id);
            
            // Créer une URL locale à partir du blob et déclencher le téléchargement
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.name || 'fichier';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Nettoyer l'URL blob après utilisation
            URL.revokeObjectURL(blobUrl);
            
            console.log('Fichier téléchargé avec succès');
        } catch (error) {
            console.error('Erreur lors du téléchargement du fichier:', error);
        }
    };

    const handlePreviewFile = (file) => {
        setPreviewFile(file);
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
    };

    const isPreviewable = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'stl'].includes(extension);
    };

    // Au moins un travail avec « Modèle 3D souhaité » : design 3D à réaliser par le labo
    // (indépendant des fichiers joints, que le client fournit ou pas).
    const needsDigitalModel = order.works?.some(w => w.hasDigitalModel);

    return (
        <>
            {/* Ligne principale du tableau */}
            <tr className="hover:bg-white/10 border-b border-white/10 cursor-pointer transition-colors"
                onClick={() => onOrderClick && onOrderClick(order)}>
                {/* Bouton d'expansion */}
                <td className="px-4 py-4 w-12">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                        className="text-gray-500 hover:text-gray-300 transition-colors"
                    >
                        {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </button>
                </td>

                {/* Numéro de commande */}
                <td className="px-4 py-4 font-medium text-white whitespace-nowrap">
                    <div className="flex items-center gap-2">
                        <NotificationBadge count={unreadCount} size="small" />
                        {formatOrderRef(order)}
                    </div>
                </td>

                {/* Client */}
                <td className="px-4 py-4">
                    <div className="text-sm font-medium text-white">{order.clientInfo.name}</div>
                    <div className="text-xs text-gray-400">{order.clientInfo.clinic}</div>
                </td>

                {/* Patient */}
                <td className="px-4 py-4">
                    {order.patient_name_code ? (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onPatientClick && onPatientClick({
                                    patientNameCode: order.patient_name_code,
                                    clientName: order.client_name
                                });
                            }}
                            className="text-sm font-medium text-yellow-400 hover:text-yellow-300 hover:underline cursor-pointer transition-colors flex items-center"
                            title="Voir l'historique des commandes pour ce patient"
                        >
                            <User className="h-3 w-3 mr-1" />
                            {order.patient_name_code}
                        </button>
                    ) : (
                        <span className="text-sm text-gray-500 italic">Non renseigné</span>
                    )}
                </td>

                {/* Type de prothese */}
                <td className="px-4 py-4 text-sm text-gray-300">
                    {order.works && order.works.length > 0 ? (
                        <div>
                            <div className="font-medium flex items-center gap-1.5">
                                {PROSTHESIS_TYPE_LABELS[order.works[0].prosthesisType]}
                                {needsDigitalModel && (
                                    <span title="Modèle 3D souhaité" className="text-yellow-400">
                                        <Box className="h-4 w-4" />
                                    </span>
                                )}
                            </div>
                            {order.works[0].prosthesisSubtype && (
                                <div className="text-xs text-gray-400">{getSubtypeLabel(order.works[0].prosthesisType, order.works[0].prosthesisSubtype)}</div>
                            )}
                            {order.works.length > 1 && (
                                <div className="text-xs text-yellow-400 mt-1">+{order.works.length - 1} autre{order.works.length > 2 ? 's' : ''}</div>
                            )}
                            {order.works.flatMap(w => w.toothNumbers || []).length > 0 && (
                                <div className="text-xs text-gray-400 mt-1">🦷 {order.works.flatMap(w => w.toothNumbers || []).join(', ')}</div>
                            )}
                        </div>
                    ) : (
                        <span className="text-gray-500 italic">-</span>
                    )}
                </td>

                {/* Description courte */}
                <td className="px-4 py-4 text-sm text-gray-400 max-w-xs truncate" title={order.works?.[0]?.description || ''}>
                    {order.works && order.works.length > 0 ? (
                        order.works[0].description || <span className="italic">Pas de description</span>
                    ) : (
                        <span className="italic">-</span>
                    )}
                </td>

                {/* Date de commande */}
                <td className="px-4 py-4 text-sm text-gray-400 whitespace-nowrap">
                    {formatDate(order.orderDate)}
                </td>

                {/* Date de livraison */}
                <td className="px-4 py-4 text-sm text-gray-400 whitespace-nowrap">
                    {formatDate(order.deliveryDate)}
                </td>

                {/* Statut : badge coloré interactif (clic = changer le statut) */}
                <td className="px-4 py-4">
                    <StatusSelect
                        status={order.status}
                        onChange={(s) => onStatusChange(order.id, s)}
                    />
                </td>
            </tr>

            {/* Ligne expandée avec détails */}
            {isExpanded && (
                <tr className="bg-black/30">
                    <td colSpan="9" className="px-6 py-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Informations detaillees du client */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-white text-sm">Informations du client</h4>
                                <div className="glass border border-white/10 p-3 rounded-md space-y-2">
                                    <div className="flex items-center text-sm">
                                        <Phone className="h-4 w-4 text-gray-500 mr-2" />
                                        <span className="text-gray-300">{order.clientInfo.phone}</span>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <Mail className="h-4 w-4 text-gray-500 mr-2" />
                                        <span className="text-gray-300">{order.clientInfo.email}</span>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <MapPin className="h-4 w-4 text-gray-500 mr-2" />
                                        <span className="text-gray-300">{order.clientInfo.address}</span>
                                    </div>
                                </div>

                                {/* Details travaux */}
                                <h4 className="font-medium text-white text-sm">Travaux ({order.works?.length || 0})</h4>
                                {order.works && order.works.length > 0 ? (
                                    <div className="space-y-2">
                                        {order.works.map((work, index) => (
                                            <div key={index} className="glass rounded-md border border-white/10 overflow-hidden">
                                                <div className="bg-indigo-900/30 px-3 py-1.5 border-b border-white/10 flex items-center justify-between">
                                                    <span className="text-xs text-indigo-300 font-medium">Travail {index + 1}</span>
                                                    <span className="text-xs text-gray-200 font-medium">
                                                        {PROSTHESIS_TYPE_LABELS[work.prosthesisType]}
                                                        {work.prosthesisSubtype && ` - ${getSubtypeLabel(work.prosthesisType, work.prosthesisSubtype)}`}
                                                    </span>
                                                </div>
                                                <div className="p-3">
                                                    <div className="flex flex-wrap gap-2 mb-2">
                                                        <span className="inline-flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded text-xs text-gray-200">
                                                            🦷 {work.toothNumbers?.join(', ') || '-'}
                                                        </span>
                                                        {work.shade && (
                                                            <span className="inline-flex items-center gap-1 bg-blue-900/40 px-2 py-0.5 rounded text-xs text-blue-200">
                                                                🎨 {work.shade}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {work.description && (
                                                        <p className="text-xs text-gray-400 leading-relaxed">{work.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="glass border border-white/10 p-3 rounded-md text-center text-sm text-gray-500">
                                        Aucun travail défini
                                    </div>
                                )}
                            </div>

                            {/* Fichiers joints */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-white text-sm">Fichiers joints</h4>
                                {order.files && order.files.length > 0 ? (
                                    <div className="glass border border-white/10 p-3 rounded-md space-y-2">
                                        {order.files.map((file, index) => (
                                            <div key={index} className="flex items-center justify-between bg-black/30 border border-white/10 p-2 rounded">
                                                <div className="flex items-center">
                                                    <FileText className="h-4 w-4 text-gray-500 mr-2" />
                                                    <span className="text-sm text-gray-300">{file.name}</span>
                                                    <span className="text-xs text-gray-500 ml-2">({file.size})</span>
                                                </div>
                                                <div className="flex items-center space-x-1">
                                                    {isPreviewable(file.name) && (
                                                        <button
                                                            onClick={() => handlePreviewFile(file)}
                                                            className="text-yellow-400 hover:text-yellow-300 p-1 rounded hover:bg-yellow-500/20 transition-colors"
                                                            title="Prévisualiser"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDownload(file)}
                                                        className="text-green-400 hover:text-green-300 p-1 rounded hover:bg-green-500/20 transition-colors"
                                                        title="Télécharger"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="glass border border-white/10 p-3 rounded-md text-center text-sm text-gray-500">
                                        Aucun fichier joint
                                    </div>
                                )}

                            </div>
                        </div>
                    </td>
                </tr>
            )}

            {/* Modal de prévisualisation */}
            {previewFile && (
                <FilePreview
                    file={previewFile}
                    onClose={handleClosePreview}
                />
            )}
        </>
    );
};

export default OrderTableRow;